import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { EStatusCode } from '../../api/enum';
import * as ProjectHelper from '../../api/project/projectHelper';
import { route } from '../../route/Index';
import isAdmin from '../../shared/hoc/isAdmin';
import InputDrainageTrench from '../../shared/input/drainageTrench/InputDrainageTrench';
import InputDischarge from '../../shared/input/discharge/InputDischarge';
import InputLoad from '../../shared/input/InputLoad';
import InputSlope from '../../shared/input/InputSlope';
import InputSoil from '../../shared/input/soil/InputSoil';
import variable from '../../shared/style/variable';
import { toastAlert } from '../../shared/util';
import { EReduxState } from '../../store/enum';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { selectProject, setProject } from '../../store/project/projectSlice';
import { EUnitOfMeasure } from '../../shared/enum';
import { LoadingButton } from '@mui/lab';

interface IProjectTemplateDetailProps {}

const ProjectTemplateContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

const ProjectTemplateDetail = (props: IProjectTemplateDetailProps) => {
    let { param } = useParams();

    const navigate = useNavigate();

    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaveLoading, setSaveIsLoading] = React.useState(false);

    const project = useAppSelector(selectProject);
    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    React.useEffect(() => {
        const getProjectTemplatesAsync = async () => {
            setIsLoading(true);
            let projectTemplates = await ProjectHelper.getProjectTemplatesAsync();
            let projectTemplate = projectTemplates?.data.find(x => x.projectInfo.unitOfMeasure == param);
            setIsLoading(false);
            dispatch(setProject(projectTemplate!));
        };
        if (project.status == EReduxState.initialState) {
            getProjectTemplatesAsync();
        }
    }, []);

    const handleClickSave = async () => {
        setSaveIsLoading(true)
        let resUpdate = await ProjectHelper.updateProjectAsync(project.data!);
        if (resUpdate.status == EStatusCode.Ok) {
            toastAlert('success', t('updatedProjectTemplate'));
        }
        setSaveIsLoading(false)
    };

    return (
        <>
            <Stack direction={'row'} padding={'0 1rem'} alignItems={'center'}>
                <Link to={route.backoffice} style={{ textDecoration: 'none' }}>
                    <Stack alignItems={'center'} direction={'row'}>
                        <ArrowBackIosIcon style={{ fill: variable.greenLight }} />
                        <Typography variant='h6' padding={'1rem 0'} color={variable.greenLight}>
                            {t('back')}
                        </Typography>
                    </Stack>
                </Link>
                <Stack width={'100%'} textAlign={'center'}>
                    <Typography variant='h6' color={variable.greenLight}>
                        {t('updateProjectTemplate', { unitOfMeasure: parseInt(param!) == EUnitOfMeasure.Metric ? t('metric') : t('imperial') })}
                    </Typography>
                </Stack>
            </Stack>
            {isLoading && <CircularProgress />}
            {!isLoading && project.status == EReduxState.fulfilled && (
                <ProjectTemplateContainer maxWidth={'xl'}>
                    <Stack height={'95%'} direction='column'>
                        <Stack height={'100%'} direction={'row'} spacing={1}>
                            <Stack width={'20%'} position='relative'>
                                <Stack variant='scrollable'>
                                    <InputSlope />
                                </Stack>
                            </Stack>
                            <Stack width={'20%'} position='relative'>
                                <Stack variant='scrollable'>
                                    <InputLoad />
                                </Stack>
                            </Stack>
                            <Stack width={'20%'} position='relative'>
                                <Stack variant='scrollable'>
                                    <InputSoil />
                                </Stack>
                            </Stack>
                            <Stack width={'20%'} position='relative'>
                                <Stack variant='scrollable'>
                                    <InputDischarge />
                                </Stack>
                            </Stack>
                            <Stack width={'20%'} position='relative'>
                                <Stack variant='scrollable'>
                                    <InputDrainageTrench />
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack alignItems='end'>
                            <Stack direction={'row'} spacing={2}>
                                <Button variant='white' onClick={() => navigate(route.backoffice)}>
                                    {t('back')}
                                </Button>
                                <LoadingButton loading={isSaveLoading} style={{ overflow: 'hidden' }} variant='orange' onClick={handleClickSave}>
                                    {t('save')}
                                </LoadingButton>
                            </Stack>
                        </Stack>
                    </Stack>
                </ProjectTemplateContainer>
            )}
        </>
    );
};

export default isAdmin(ProjectTemplateDetail);
